import React, { createContext, useContext, useState, useEffect } from 'react';
import { useHairSession } from './HairSessionContext';
import { SESSION_APP_DATA_BASE } from '../config/sessionAppData';
import { getPlanFlags } from '../config/hairAnalysisFeatureFlags';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';

const HairAnalysisPlanContext = createContext(null);

function normalizeCaptureMode(value) {
  if (value == null || value === '') return null;
  const v = String(value).toLowerCase().trim();
  return v === 'side' || v === 'basic' ? v : null;
}

export function HairAnalysisPlanProvider({ children }) {
  const { token } = useHairSession();
  const [plan] = useState(null);
  const [captureMode, setCaptureMode] = useState(null);
  const [aiMode, setAiMode] = useState(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!token || !token.trim()) {
      setCaptureMode(null);
      setAiMode(null);
      setIsReady(true);
      return;
    }

    let cancelled = false;

    const applyCaptureMode = (result) => {
      if (cancelled) return;
      const rawCaptureMode =
        result?.capture_mode ??
        result?.data?.capture_mode ??
        (Array.isArray(result) && result[0]?.capture_mode);
      setCaptureMode(normalizeCaptureMode(rawCaptureMode));
      setIsReady(true);
    };

    const applyAiMode = (result) => {
      if (cancelled) return;
      const rawAiMode =
        result?.ai_mode ??
        result?.data?.ai_mode ??
        (Array.isArray(result) && result[0]?.ai_mode);
      const mode = rawAiMode && ['advanced', 'basic'].includes(String(rawAiMode).toLowerCase().trim())
        ? String(rawAiMode).toLowerCase().trim()
        : null;
      setAiMode(mode);
    };

    const captureUrl = `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/capture`;
    const summaryUrl = `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/summary`;

    fetchWithTimeout(captureUrl, { timeoutMs: 20000 })
      .then((res) => (res.ok ? res.json() : null))
      .then((result) => {
        if (cancelled) return;
        applyCaptureMode(result);
      })
      .catch(() => {
        if (!cancelled) {
          setCaptureMode(null);
          setIsReady(true);
        }
      });

    fetchWithTimeout(summaryUrl, { timeoutMs: 20000 })
      .then((res) => (res.ok ? res.json() : null))
      .then((result) => {
        if (cancelled) return;
        applyAiMode(result);
      })
      .catch(() => {
        if (!cancelled) {
          setAiMode(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const flags = getPlanFlags(plan);
  const enableSideCapture = captureMode === 'side';
  const enableOllama = aiMode === 'advanced';
  const value = {
    plan,
    captureMode,
    aiMode,
    isReady,
    ...flags,
    ENABLE_SIDE_CAPTURE: enableSideCapture,
    ENABLE_OLLAMA_DESCRIPTIONS: enableOllama,
    ENABLE_OLLAMA_SUMMARY: enableOllama,
    ENABLE_OLLAMA_ROUTINE: enableOllama,
  };

  return (
    <HairAnalysisPlanContext.Provider value={value}>
      {children}
    </HairAnalysisPlanContext.Provider>
  );
}

export function useHairAnalysisPlan() {
  const ctx = useContext(HairAnalysisPlanContext);
  if (!ctx) {
    throw new Error('useHairAnalysisPlan must be used within HairAnalysisPlanProvider');
  }
  return ctx;
}
